import { Box, Card, CircularProgress, Grid, Icon, IconButton, InputLabel } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { Form, Formik, useFormikContext } from "formik";
import FormField from "layouts/pages/users/new-user/components/FormField";
import PropTypes from "prop-types";
import { memo, useEffect, useState } from 'react';
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from 'yup';

const IndiceCreateEdit = ({ id, closeCreateEdit }) => {
    const { tipoIndiceEnum } = useUser();

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [isLoadingInfo, setIsLoadingInfo] = useState(false);

    const handleSubmit = (values, actions) => {

        const body = {
            id: values.id,
            tipo: values.tipo.code,
            date: values.data,
            value: values.valor
        };

        postNoResponse("/indice/save", body)
            .then(() => {
                successAlert(!id ? "Índice criado com sucesso!" : "Alterações salvas!", handleSucess());
            })
            .catch(() => {
                errorAlert(!id ? "Erro ao criar índice!" : 'Erro ao salvar alterações.');
            })
            .finally(() => {
                actions.setSubmitting(false);
            });
    };

    function handleSucess() {
        closeCreateEdit()
    }

    return (
        <Card id="user-info">
            <Box display="flex" alignItems="center" p={3}>
                <IconButton onClick={closeCreateEdit} sx={{ p: 0, paddingRight: 1, paddingTop: 0.75 }}>
                    <MDTypography variant="h5">
                        <Icon>arrow_back</Icon>
                    </MDTypography>
                </IconButton>
                <MDTypography variant="h6">{id ? "Editar" : "Adicionar"} Índice</MDTypography>
            </Box>
            <Formik
                initialValues={{
                    id: null,
                    tipo: null,
                    data: '',
                    valor: 0.00,
                }}
                validationSchema={validationSchema}
                onSubmit={(values, actions) => {
                    actions.setSubmitting(true);
                    handleSubmit(values, actions);
                }}>
                {({ values, errors, touched, isSubmitting, setFieldValue }) => {
                    return (
                        <Form id={configForm.formId} autoComplete="off">
                            <FetchAndSetData
                                id={id}
                                setIsLoadingInfo={setIsLoadingInfo}
                                closeCreateEdit={closeCreateEdit}
                                tipoIndiceEnum={tipoIndiceEnum} />

                            <MDBox px={3}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Tipo Índice</InputLabel>
                                        <MDSelect
                                            isLoading={isLoadingInfo}
                                            getOptionValue={option => option.code}
                                            getOptionLabel={option => option.name}
                                            name={tipo.name}
                                            placeholder={tipo.placeholder}
                                            value={values.tipo}
                                            options={tipoIndiceEnum}
                                            onChange={selectedOptions => {
                                                setFieldValue('tipo', selectedOptions);
                                            }}
                                        />
                                        {errors.tipo && touched.tipo && (
                                            <MDTypography variant="caption" color="error">
                                                {errors.tipo}
                                            </MDTypography>
                                        )}
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormField
                                            loading={isLoadingInfo}
                                            type='number'
                                            label='Valor'
                                            name='valor'
                                            value={values.valor}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>

                                        <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data</InputLabel>
                                        <MDTextFieldCalendar
                                            type="date"
                                            value={values.data || ""}
                                            fullWidth
                                            onChange={({ target }) => {
                                                setFieldValue("data", target.value)
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            darkMode={darkMode}
                                        />
                                    </Grid>
                                </Grid>
                            </MDBox>
                            <MDBox display="flex" alignItems="center" justifyContent="end" p={2}>
                                <MDButton
                                    disabled={isLoadingInfo}
                                    size="large"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    type="submit"
                                    endIcon={isSubmitting ?
                                        <CircularProgress
                                            color="inherit"
                                            size={20} sx={{ ml: 0, mr: 0 }} /> : null}>
                                    {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                                </MDButton>
                            </MDBox>
                        </Form>)
                }}
            </Formik>
        </Card>
    );
};

IndiceCreateEdit.propTypes = {
    id: PropTypes.string,
    closeCreateEdit: PropTypes.func.isRequired,
};

export default memo(IndiceCreateEdit);

const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit, tipoIndiceEnum }) => {
    const { setFieldValue } = useFormikContext();

    useEffect(() => {
        if (id) {
            setIsLoadingInfo(true);
            get(`/indice/by-id/${id}`).then(data => {
                const { id, tipo, date, value } = data;
                setFieldValue('tipo', tipoIndiceEnum.find(x => x.code == tipo));
                setFieldValue('id', id);
                setFieldValue('data', date);
                setFieldValue('valor', value);
                setIsLoadingInfo(false);
            }).catch((erro) => errorAlert("Índice não encontrado!", closeCreateEdit));
        }
    }, [id]);

    return null;
};

export const configForm = {
    formId: "configuration-form",
    formField: {
        tipo: {
            name: "tipo",
            label: "Tipo de Índice",
            placeholder: "Selecione",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        data: {
            name: "data",
            label: "Data",
            placeholder: "Informe a data...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
        valor: {
            name: "valor",
            label: "Valor",
            placeholder: "Informe o valor...",
            errorMsg: "Campo Obrigatório. Somente espaços em brancos não são permitidos.",
        },
    }
}

const {
    formField: {
        tipo,
        data,
        valor,
    },
} = configForm;

export const validationSchema = Yup.object().shape({

});
